// Display formatters shared across the app pages. These carry the app's
// table/card display conventions — an em-dash ("—") for empty/invalid values and
// fixed en-US short formats — that several pages render identically today. One
// home so the behavior stays in a single tested place. (Intl-based currency/
// number helpers were deliberately not adopted: their output mismatched these
// conventions.)


const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];


/**
 * Whole-dollar money, e.g. "$1,250". A null/undefined/'' value renders "$0"
 * (0 is a valid amount). Pass `{ dash: true }` to render "—" for an empty value
 * instead — the donor-ROI columns want a dash, not "$0".
 */
function formatMoney(value, { dash = false } = {}) {
  if (dash && (value === null || value === undefined || value === '')) {
    return '—';
  }
  return `$${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}


/**
 * Price-or-dash: "$12" for integers, "$12.50" for fractional amounts. An empty
 * ('' / null / undefined) or non-numeric value renders the `empty` placeholder
 * (default "—"); pass `{ empty: '' }` for the blank-when-empty form the Mark-Sold
 * summary uses.
 */
function formatPrice(price, { empty = '—' } = {}) {
  if (price === null || price === undefined || price === '') {
    return empty;
  }
  const value = Number(price);
  if (Number.isNaN(value)) {
    return empty;
  }
  return `$${Number.isInteger(value) ? value : value.toFixed(2)}`;
}


/**
 * Short date label. Default "Jun 9" (month + day); pass `{ withYear: true }` for
 * the locale-stable "Jun 3, 2026" form used by Settings/Billing. A missing or
 * unparseable value renders "—".
 */
function formatDate(value, { withYear = false } = {}) {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  if (withYear) {
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  return `${MONTHS[date.getMonth()]} ${date.getDate()}`;
}


export { formatDate, formatMoney, formatPrice };
